import { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';

export default function Groups() {
  const [tenantId, setTenantId] = useState<string>('');
  const [groups, setGroups] = useState<any[]>([]);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [showSelection, setShowSelection] = useState(false);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fetchingAvailable, setFetchingAvailable] = useState(false);

  // Get current user's tenant on mount
  useEffect(() => {
    apiClient.get('/api/auth/me')
      .then((data) => {
        if (data.success) {
          setTenantId(data.data.tenantId);
        }
      })
      .catch((error) => console.error('Failed to get user info:', error));
  }, []);

  // Load groups when tenant changes
  useEffect(() => {
    if (tenantId) {
      loadGroups();
    }
  }, [tenantId]);

  const loadGroups = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await apiClient.get(`/api/groups/${tenantId}`);
      if (data.success) {
        setGroups(data.data);
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
    setLoading(false);
  };

  const fetchAvailableGroups = async () => {
    if (!tenantId) return;

    setFetchingAvailable(true);
    try {
      // Fetch available groups from Graph
      const availableData = await apiClient.get(`/api/groups/available/${tenantId}`);
      
      // Fetch already synced groups from database
      const syncedData = await apiClient.get(`/api/groups/${tenantId}?limit=1000`);
      
      if (availableData.success) {
        // Sort groups alphabetically by displayName
        const sortedGroups = availableData.data.sort((a: any, b: any) => 
          a.displayName.localeCompare(b.displayName)
        );
        setAvailableGroups(sortedGroups);
        
        // Pre-select groups that are already synced
        if (syncedData.success) {
          const syncedGroupIds = new Set(
            syncedData.data.map((g: any) => g.graphGroupId)
          );
          setSelectedGroupIds(syncedGroupIds);
        } else {
          setSelectedGroupIds(new Set()); // No groups synced yet
        }
        
        setShowSelection(true);
        setGroupSearchQuery(''); // Reset search
      } else {
        alert(`Failed to fetch groups: ${availableData.error}`);
      }
    } catch (error: any) {
      alert(`Failed to fetch groups: ${error.message}`);
    }
    setFetchingAvailable(false);
  };

  const handleSync = async () => {
    if (!tenantId || selectedGroupIds.size === 0) {
      alert('Please select at least one group to sync');
      return;
    }

    setSyncing(true);
    try {
      const data = await apiClient.post(`/api/groups/sync/${tenantId}`, {
        groupIds: Array.from(selectedGroupIds),
      });

      if (data.success) {
        alert(
          `Sync complete!\n\nGroups: ${data.data.groupsSync.synced} synced (${data.data.groupsSync.created} created, ${data.data.groupsSync.updated} updated)\n\nMembers: ${data.data.membersSync.totalAdded} added, ${data.data.membersSync.totalRemoved} removed`
        );
        setShowSelection(false);
        setAvailableGroups([]);
        setSelectedGroupIds(new Set());
        loadGroups();
      } else {
        alert(`Sync failed: ${data.error}`);
      }
    } catch (error: any) {
      alert(`Sync failed: ${error.message}`);
    }
    setSyncing(false);
  };

  const toggleGroupSelection = (groupId: string) => {
    const newSelection = new Set(selectedGroupIds);
    if (newSelection.has(groupId)) {
      newSelection.delete(groupId);
    } else {
      newSelection.add(groupId);
    }
    setSelectedGroupIds(newSelection);
  };

  // Filter groups by search query
  const filteredGroups = availableGroups.filter(group =>
    group.displayName.toLowerCase().includes(groupSearchQuery.toLowerCase()) ||
    (group.description && group.description.toLowerCase().includes(groupSearchQuery.toLowerCase()))
  );

  return (
    <div>
      {/* Group Selection Modal */}
      {showSelection && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            maxHeight: '80vh',
            width: '90%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 10px' }}>Select Groups to Sync</h3>
              <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>
                Choose which groups and their members to sync from Microsoft 365
              </p>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={groupSearchQuery}
                  onChange={(e) => setGroupSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                {selectedGroupIds.size} of {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''} selected
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredGroups.map((group) => (
                  <label
                    key={group.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      background: selectedGroupIds.has(group.id) ? '#eff6ff' : '#f9fafb',
                      border: `2px solid ${selectedGroupIds.has(group.id) ? '#3b82f6' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroupIds.has(group.id)}
                      onChange={() => toggleGroupSelection(group.id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500' }}>{group.displayName}</div>
                      {group.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {group.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowSelection(false);
                  setAvailableGroups([]);
                  setSelectedGroupIds(new Set());
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSync}
                disabled={syncing || selectedGroupIds.size === 0}
                style={{
                  padding: '10px 20px',
                  background: syncing || selectedGroupIds.size === 0 ? '#ccc' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: syncing || selectedGroupIds.size === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                }}
              >
                {syncing ? 'Syncing...' : `Sync ${selectedGroupIds.size} Group${selectedGroupIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 10px' }}>Groups</h2>
        <p style={{ color: '#666', margin: 0 }}>
          Manage synced groups from Microsoft 365
        </p>
      </div>

      {/* Sync Button */}
      <div
        style={{
          marginBottom: '25px',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <button
          onClick={fetchAvailableGroups}
          disabled={fetchingAvailable || !tenantId}
          style={{
            padding: '12px 24px',
            background: fetchingAvailable ? '#ccc' : '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: fetchingAvailable ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '15px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          {fetchingAvailable ? '⏳ Loading...' : '🔄 Select Groups to Sync'}
        </button>
      </div>

      {/* Stats */}
      <div
        style={{
          background: 'white',
          padding: '15px 20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '20px',
        }}
      >
        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
          Total Groups
        </div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
          {groups.length}
        </div>
      </div>

      {/* Groups Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '15px',
        }}
      >
        {loading ? (
          <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading groups...
          </div>
        ) : groups.length === 0 ? (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: '40px',
              textAlign: 'center',
              color: '#666',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            No groups found. Click the button above to select and sync groups from Microsoft 365.
          </div>
        ) : (
          groups.map((group) => (
            <div
              key={group.id}
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                <h3
                  style={{
                    margin: '0 0 5px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                  }}
                >
                  {group.name}
                </h3>
                {group.description && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#6b7280',
                      lineHeight: '1.4',
                    }}
                  >
                    {group.description}
                  </p>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  👥 {group._count?.members || 0} members
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {group.lastSyncedAt
                    ? new Date(group.lastSyncedAt).toLocaleDateString()
                    : 'Not synced'}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
